import React, { useEffect, useState, useCallback } from "react";
import {
  FolderKanban, CheckSquare, Clock, Star, Users,
  TrendingUp, BarChart2, Award, AlertTriangle,
  Ticket, ChevronUp, ChevronDown,
} from "lucide-react";
import api from "../api/api";
import { useAuth } from "../contexts/AuthContext";
import PerformanceWidget from "../components/PerformanceWidget";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,'0')}`; };


/* ── palette ──────────────────────────────────────────────────── */
const STATUS_COLORS: Record<string, string> = {
  TODO:        "#94A3B8",
  OPEN:        "#94A3B8",
  IN_PROGRESS: "#3B82F6",
  ON_HOLD:     "#F59E0B",
  COMPLETED:   "#10B981",
  CLOSED:      "#10B981",
  PENDING:     "#F59E0B",
};
const PIE_COLORS = ["#94A3B8", "#3B82F6", "#F59E0B", "#10B981"];

const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* ── helpers ──────────────────────────────────────────────────── */
const RingProgress = ({ pct, size = 56, stroke = 6, color = "#3B82F6" }: any) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
    </svg>
  );
};

/* ── KPI Card ─────────────────────────────────────────────────── */
const KpiCard = ({ label, value, sub, icon: Icon, accent, pct }: any) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent}`}>
      <Icon size={22} className="text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
    {pct !== undefined && (
      <div className="relative flex items-center justify-center">
        <RingProgress pct={pct} color={pct >= 70 ? "#10B981" : pct >= 40 ? "#F59E0B" : "#EF4444"} />
        <span className="absolute text-[10px] font-bold text-gray-700">{pct}%</span>
      </div>
    )}
  </div>
);

/* ── ProgressBar ──────────────────────────────────────────────── */
const ProgressBar = ({ pct, color = "bg-blue-500" }: any) => (
  <div className="w-full bg-gray-100 rounded-full h-2">
    <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

/* ══════════════════════════════════════════════════════════════ */
const Dashboard: React.FC = () => {
  const { user: currentUser, isAdmin, isManager, isDeptHead } = useAuth();
  const canSeeAll = isAdmin || isManager || isDeptHead;
  const [data, setData]           = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const now = new Date();
  const [dept, setDept]           = useState("");
  const [month, setMonth]         = useState<string>(String(now.getMonth() + 1));
  const [year, setYear]           = useState<string>(String(now.getFullYear()));
  const [userId, setUserId]       = useState("");
  const [users, setUsers]         = useState<any[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    const params: any = {};
    if (dept)   params.department = dept;
    if (month)  params.month = month;
    if (year)   params.year  = year;
    // Non-admin/manager/deptHead users always see only their own data
    if (!canSeeAll && currentUser?.id) {
      params.userId = currentUser.id;
    } else if (userId) {
      params.userId = userId;
    }
    api.get("/reports/dashboard", { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [dept, month, year, userId, canSeeAll, currentUser]);

  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, [load]);

  /* ── loading skeleton ─────────────────────────────────────── */
  if (loading && !data)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );

  const d = data || {};
  const taskStatusData = (d.tasksByStatus || []).map((s: any) => ({
    name: s.status.replace("_", " "),
    value: s._count.id,
  }));
  const deptData = (d.ticketsByDept || []).map((x: any) => ({
    name: (x.department || "N/A").slice(0, 12),
    tickets: x._count.id,
  }));
  const workloadData = (d.workloadData || []).slice(0, 8);
  const topPerformers = d.topPerformers || [];
  const activeProjects = d.activeProjects || [];
  const employees = d.employeePerformance || [];

  const DEPARTMENTS = ["Technical Support","Development","Customer Support","Billing","Other"];
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="space-y-6 pb-8">

      {/* ── Performance Widget (top) ─────────────────────────── */}
      <PerformanceWidget
        periodOverride={month && year ? `${year}-${String(month).padStart(2,"0")}` : undefined}
        onPeriodChange={(p) => {
          const [y, m] = p.split("-");
          if (y && m) { setYear(y); setMonth(String(Number(m))); }
        }}
      />

      {/* ── Header + Filters ────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500 mt-0.5">Performance overview & analytics</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={dept} onChange={e => setDept(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
            <option value="">All Departments</option>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={month} onChange={e => setMonth(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
            <option value="">All Months</option>
            {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
            <option value="">All Years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={userId} onChange={e => setUserId(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
            <option value="">All Employees</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {(dept || month || year || userId) && (
            <button onClick={() => { setDept(""); setMonth(""); setYear(""); setUserId(""); }}
              className="px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
              Reset
            </button>
          )}
        </div>
      </div>

      {/* ── KPI Cards ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Projects" value={d.totalProjects ?? 0}
          sub={`${d.completedProjects ?? 0} completed`}
          icon={FolderKanban} accent="bg-blue-500"
          pct={d.projectCompletion ?? 0}
        />
        <KpiCard
          label="Project Completion" value={`${d.projectCompletion ?? 0}%`}
          sub={`${d.completedProjects ?? 0} / ${d.totalProjects ?? 0} projects`}
          icon={TrendingUp} accent="bg-emerald-500"
          pct={d.projectCompletion ?? 0}
        />
        <KpiCard
          label="Task Completion" value={`${d.taskCompletion ?? 0}%`}
          sub={`${d.completedTasks ?? 0} / ${d.totalTasks ?? 0} tasks`}
          icon={CheckSquare} accent="bg-violet-500"
          pct={d.taskCompletion ?? 0}
        />
        <KpiCard
          label="Total Hours Logged" value={`${d.totalHours ?? 0}h`}
          sub={`${d.totalTickets ?? 0} tickets · ${d.openTickets ?? 0} open`}
          icon={Clock} accent="bg-orange-500"
        />
      </div>

      {/* Overdue + Quality row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className={`rounded-2xl border p-5 shadow-sm ${
          (d.overdueTickets ?? 0) > 0
            ? "bg-red-50 border-red-200"
            : "bg-white border-gray-100"
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${(d.overdueTickets ?? 0) > 0 ? "text-red-700" : "text-gray-600"}`}>
              🔴 Overdue Tickets
            </span>
            <span className="text-[11px] text-gray-500">age &gt; 24h</span>
          </div>
          <div className={`text-3xl font-bold ${(d.overdueTickets ?? 0) > 0 ? "text-red-700" : "text-gray-800"}`}>
            {d.overdueTickets ?? 0}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Open tickets past 24h SLA in this period
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">⭐ Quality Reviews</span>
            <span className="text-[11px] text-gray-500">this period</span>
          </div>
          <div className="flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-3xl font-bold text-gray-900">{d.qualityStats?.total ?? 0}</div>
              <div className="text-xs text-gray-500">total reviews</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-700">{d.qualityStats?.avg ?? 0}</div>
              <div className="text-xs text-gray-500">avg score</div>
            </div>
            <div className="flex gap-2 ml-auto">
              <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2 py-1 rounded-md text-xs font-medium">High: {d.qualityStats?.high ?? 0}</span>
              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-md text-xs font-medium">Medium: {d.qualityStats?.medium ?? 0}</span>
              <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded-md text-xs font-medium">Low: {d.qualityStats?.low ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Charts Row ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Opened vs Closed — Projects & Tasks */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="font-semibold text-gray-800 mb-1">Opened vs Closed</h3>
          <p className="text-xs text-gray-400 mb-4">Projects & Tasks activity this period</p>
          {(() => {
            const oc = d.openedClosed || [];
            const total = oc.reduce((s: number, r: any) => s + (r.opened || 0) + (r.closed || 0), 0);
            if (total === 0) {
              return <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data</div>;
            }
            return (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={oc} barSize={38} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="opened" name="Opened" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="closed" name="Closed" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            );
          })()}
        </div>

        {/* Workload Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 lg:col-span-2">
          <h3 className="font-semibold text-gray-800 mb-1">Workload Overview</h3>
          <p className="text-xs text-gray-400 mb-4">Hours logged per employee</p>
          {workloadData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={workloadData} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="hours" name="Hours" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Middle Row ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Top Performers */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Award size={18} className="text-yellow-500" />
            <h3 className="font-semibold text-gray-800">Top Performers</h3>
          </div>
          {topPerformers.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No data</p>
          ) : (
            <div className="space-y-3">
              {(() => {
                const maxPts = Math.max(...topPerformers.map((e: any) => e.totalPoints ?? 0), 1);
                return topPerformers.map((e: any, i: number) => {
                  const pts = e.totalPoints ?? 0;
                  const pctBar = Math.min(100, Math.round((pts / maxPts) * 100));
                  const barColor = pts >= 120 ? "bg-emerald-500" : pts >= 100 ? "bg-blue-500" : pts >= 80 ? "bg-yellow-400" : pts >= 60 ? "bg-orange-400" : "bg-red-400";
                  const tagColor = pts >= 120 ? "bg-emerald-100 text-emerald-700" : pts >= 100 ? "bg-blue-100 text-blue-700" : pts >= 80 ? "bg-yellow-100 text-yellow-700" : pts >= 60 ? "bg-orange-100 text-orange-700" : "bg-red-100 text-red-600";
                  return (
                    <div key={e.id} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0
                        ${i === 0 ? "bg-yellow-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-blue-100 text-blue-600"}`}>
                        {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{e.name}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <ProgressBar pct={pctBar} color={barColor} />
                        </div>
                      </div>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${tagColor}`}>
                        {pts.toFixed(1)}
                      </span>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        {/* Employee Activity */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 lg:col-span-2">
          <h3 className="font-semibold text-gray-800 mb-1">Employee Activity</h3>
          <p className="text-xs text-gray-400 mb-4">Tickets / Tasks / Projects worked on this period</p>
          {(!d.employeeActivity || d.employeeActivity.length === 0) ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No activity data</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(260, d.employeeActivity.length * 22 + 90)}>
              <BarChart data={d.employeeActivity} barSize={12} margin={{ top: 8, right: 12, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="fullName" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  formatter={(v: any, k: any) => [v, k[0].toUpperCase() + k.slice(1)]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="tickets" name="Tickets" fill="#8B5CF6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="tasks"   name="Tasks"   fill="#3B82F6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="projects" name="Projects" fill="#10B981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Project Progress ────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <FolderKanban size={18} className="text-blue-500" />
          <h3 className="font-semibold text-gray-800">Active Project Progress</h3>
          <span className="ml-auto text-xs text-gray-400">{activeProjects.length} active projects</span>
        </div>
        {activeProjects.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No active projects</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeProjects.map((p: any) => {
              const pct = p.estimatedHours > 0
                ? Math.min(100, Math.round((p.totalLoggedMinutes / ((p.estimatedHours||1)*60)) * 100))
                : 0;
              const color = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-blue-500" : pct >= 25 ? "bg-yellow-400" : "bg-red-400";
              return (
                <div key={p.id} className="p-3 rounded-xl border border-gray-100 hover:border-blue-200 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-800 truncate max-w-[70%]">{p.name}</p>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                      ${p.status === "IN_PROGRESS" ? "bg-blue-100 text-blue-700" :
                        p.status === "PLANNING"    ? "bg-purple-100 text-purple-700" :
                        "bg-orange-100 text-orange-700"}`}>
                      {p.status.replace("_"," ")}
                    </span>
                  </div>
                  <ProgressBar pct={pct} color={color} />
                  <div className="flex justify-between mt-1.5 text-xs text-gray-400">
                    <span className="font-semibold text-gray-600">{pct}%</span>
                    <span>{fmtMin(p.totalLoggedMinutes||0)} / {p.estimatedHours||0}h est.</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Employee Performance Table ───────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 p-5 border-b border-gray-100">
          <Users size={18} className="text-violet-500" />
          <h3 className="font-semibold text-gray-800">Employee Performance</h3>
        </div>
        {employees.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">No data</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["#","Employee","Department","Tickets","Tasks","Projects","Hours","Quality","Performance"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {employees.map((e: any, i: number) => (
                  <tr key={e.id} className={`hover:bg-gray-50 transition-colors ${i === 0 ? "bg-yellow-50/50" : ""}`}>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs flex-shrink-0">
                          {e.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-800">{e.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{e.department || "—"}</td>
                    <td className="px-4 py-3 font-medium text-gray-700">{e.completedTickets}</td>
                    <td className="px-4 py-3 font-medium text-gray-700">{e.completedTasks}</td>
                    <td className="px-4 py-3 font-medium text-gray-700">{e.projectsCount ?? 0}</td>
                    <td className="px-4 py-3 font-medium text-gray-700">{e.totalHours}h</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Star size={11} className="text-yellow-400 fill-yellow-400" />
                        <span className="text-gray-700">{e.qualityCount ?? 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const pts = e.performanceScore ?? 0;
                        const maxPts = Math.max(...employees.map((x: any) => x.performanceScore ?? 0), 1);
                        const pctBar = Math.min(100, Math.round((pts / maxPts) * 100));
                        const bar = pts >= 120 ? "bg-emerald-500" : pts >= 100 ? "bg-blue-500" : pts >= 80 ? "bg-yellow-400" : pts >= 60 ? "bg-orange-400" : "bg-red-400";
                        const txt = pts >= 120 ? "text-emerald-700" : pts >= 100 ? "text-blue-700" : pts >= 80 ? "text-yellow-700" : pts >= 60 ? "text-orange-700" : "text-red-600";
                        return (
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <ProgressBar pct={pctBar} color={bar} />
                            <span className={`text-xs font-bold whitespace-nowrap ${txt}`}>
                              {pts.toFixed(1)}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default Dashboard;
